import { imgUrl } from "@/lib/api";
import { ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * O VISUAL de um banner: arte, véu e texto sobreposto.
 *
 * Existe separado do carrossel porque dois lugares precisam desenhar a MESMA
 * coisa: a home, onde o cliente vê, e a prévia do admin, onde alguém decide se
 * o texto ficou legível sobre aquela foto. Se fossem duas marcações parecidas,
 * a prévia mentiria no dia em que uma delas mudasse — e a prévia só serve
 * enquanto for exata.
 *
 * Aqui não há link, rolagem nem autoplay: isso é do carrossel. Este componente
 * é só a cara do slide.
 */

/* Duas escalas de tipografia para o mesmo desenho. A prévia do admin ocupa uns
   500px de largura, e a escala da home (até 5xl) sairia com o título cobrindo a
   arte inteira — o que faria a prévia parecer errada justamente por ser fiel. */
const ESCALA = {
  hero: {
    titulo: "font-heading text-2xl sm:text-4xl lg:text-5xl font-semibold max-w-xl leading-tight drop-shadow",
    sub: "text-sm sm:text-lg text-white/85 max-w-md leading-snug",
    cta: "mt-1 inline-flex items-center gap-2 bg-white text-slate-900 font-semibold text-sm rounded-full px-5 py-2.5 shadow-lg group-hover:bg-brand-600 group-hover:text-white transition-colors",
    caixa: "relative h-full max-w-7xl mx-auto px-6 flex flex-col justify-center items-start gap-3 text-white",
    icone: 16,
  },
  preview: {
    titulo: "font-heading text-base sm:text-xl font-semibold max-w-xs leading-tight drop-shadow",
    sub: "text-[11px] sm:text-xs text-white/85 max-w-[16rem] leading-snug line-clamp-2",
    cta: "mt-0.5 inline-flex items-center gap-1 bg-white text-slate-900 font-semibold text-[11px] rounded-full px-3 py-1.5 shadow",
    caixa: "relative h-full px-4 sm:px-5 flex flex-col justify-center items-start gap-1.5 text-white",
    icone: 12,
  },
};

export default function BannerSlide({ banner, size = "hero", eager = false }) {
  const e = ESCALA[size] || ESCALA.hero;
  const temTexto = banner.title || banner.subtitle || banner.cta_label;

  return (
    <>
      <img
        src={imgUrl(banner.image)}
        /* Sem `alt` cadastrado cai no título; sem título, string vazia —
           imagem decorativa anunciada como "imagem" só atrapalha quem usa
           leitor de tela. O que não pode é o leitor ler a URL. */
        alt={banner.alt || banner.title || ""}
        /* O primeiro slide é o que aparece de cara: carrega com prioridade e os
           outros ficam preguiçosos. Sem isso, três artes grandes competem pela
           banda no primeiro segundo da home. */
        loading={eager ? "eager" : "lazy"}
        fetchPriority={eager ? "high" : "low"}
        className="absolute inset-0 w-full h-full object-cover"
      />
      {temTexto && (
        <>
          {/* Véu só do lado do texto: escurecer a arte inteira estraga a foto
              que a loja escolheu para vender. */}
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950/80 via-slate-950/40 to-transparent" />
          <div className={e.caixa}>
            {/* `p`, e não `h2`: o texto do banner é peça de campanha, não
                estrutura da página. Como cabeçalho, os slides entravam no
                sumário do leitor de tela ANTES do <h1> da home. */}
            {banner.title && <p className={e.titulo}>{banner.title}</p>}
            {banner.subtitle && <p className={e.sub}>{banner.subtitle}</p>}
            {banner.cta_label && (
              /* `span`, não botão: na home o slide inteiro já é o link, e um
                 botão de verdade aqui viraria um clique dentro de outro. */
              <span className={cn(e.cta)}>
                {banner.cta_label} <ChevronRight size={e.icone} aria-hidden />
              </span>
            )}
          </div>
        </>
      )}
    </>
  );
}
